import { AlertCircle } from 'lucide-react'

export interface GateFailuresProps {
  failures: string[] | null | undefined
  /**
   * Optional heading text. Default: "Promotion blockers".
   */
  title?: string
  className?: string
}

/**
 * Displays per-condition gate failures as red-bordered cards. Only renders
 * for tier 2/3/4 agents — tier 1 always has an empty array, so this returns
 * null and the page collapses cleanly.
 *
 * Each entry in `failures` is a human-readable condition string from the
 * backend gate evaluator (e.g. `avg_overall=4.2 < 8.5 required`). We split
 * on the first `=` / `<` / `>` operator just to bold the LHS — if no operator
 * is found, the whole string is rendered as-is.
 */
export function GateFailures({
  failures,
  title = 'Promotion blockers',
  className,
}: GateFailuresProps) {
  if (!failures || failures.length === 0) return null

  return (
    <section
      className={`rounded-lg border border-red-900/60 bg-red-950/20 p-4${className ? ` ${className}` : ''}`}
    >
      <h2 className="mb-3 flex items-center gap-2 text-sm font-semibold text-red-200">
        <AlertCircle className="h-4 w-4" />
        {title} ({failures.length})
      </h2>
      <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
        {failures.map((f) => {
          const split = splitCondition(f)
          return (
            <div
              key={f}
              className="flex items-start gap-2 rounded border border-red-900/70 bg-red-950/40 p-3 text-xs"
            >
              <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-red-300" />
              <div className="min-w-0 font-mono text-red-100">
                {split ? (
                  <>
                    <span className="font-semibold text-red-50">{split.lhs}</span>
                    <span className="text-red-300">{split.rest}</span>
                  </>
                ) : (
                  <span>{f}</span>
                )}
              </div>
            </div>
          )
        })}
      </div>
    </section>
  )
}

// Split on the first relational operator so we can emphasize the LHS metric.
function splitCondition(s: string): { lhs: string; rest: string } | null {
  const m = s.match(/^([^=<>]+?)\s*([=<>].*)$/)
  if (!m) return null
  return { lhs: m[1], rest: ` ${m[2]}` }
}
